import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { AxiosResponse } from 'axios';
import * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ProductsContainer } from '@/components/Containers/ProductsContainer';
import { ConfirmNeededPaymentModal, HelpContainer } from '@/components/Elements';
import { ActivationNeededModal } from '@/components/Elements/ActivationNeededModal';
import { ErrorModal } from '@/components/Elements/ErrorModal';
import { FeatureComparatorLink } from '@/components/Elements/FeatureComparatorLink';
import { IncompletePaymentMethodFailedErrorModal } from '@/components/Elements/IncompletePaymentMethodFailedErrorModal';
import { IncompletePaymentRequiresActionErrorModal } from '@/components/Elements/IncompletePaymentRequiresActionErrorModal';
import { defaultModalStyles } from '@/components/Elements/Modal/customStyles';
import { PaymentsModal } from '@/components/Elements/PaymentsModal';
import { PendingPaymentErrorModal } from '@/components/Elements/PendingPaymentErrorModal';
import { ProductsHero } from '@/components/Elements/ProductsHero';
import { RequestAccountHero } from '@/components/Elements/RequestAccountHero';
import { UnpaidMeteredErrorModal } from '@/components/Elements/UnpaidMeteredErrorModal';
import { WaitProcessModal } from '@/components/Elements/WaitProcessModal';
import { FormConfirm } from '@/components/Form/FormConfirm';
import { iFormContactFields } from '@/components/Form/FormContact';
import { iFormContactGuessUserFields } from '@/components/Form/FormContactGuessUser';
import { FormContactGuessUserInModal } from '@/components/Form/FormContactGuessUserInModal';
import { FormContactInModal } from '@/components/Form/FormContactInModal';
import { COUPONS_ENABLED, PATH_UPDATE_PAYMENT_METHOD } from '@/config';
import {
  ACTIVE,
  DEFAULT_COUNTRY_ID,
  DEFAULT_PHONE,
  PRODUCT_CATEGORY_TYPE_EMAIL,
  REQUEST_SUCCESS,
} from '@/config/appConstants';
import { BillingDetails } from '@/features/billing';
import { Country } from '@/features/countries';
import { UpdatePaymentIntent } from '@/features/payment';
import { SmsCredits } from '@/features/smsCredits';
import { useMe } from '@/hooks/useMe';
import { useUserProducts } from '@/hooks/useUserProducts';
import { useAuth } from '@/lib/auth';
import {
  Currency,
  IntervalBillingMode,
  UserData,
  ProductCategoryTypes,
  ProductTypes,
  UserProductTypes,
} from '@/types';
import {
  getOrigin,
  filterUserProductsByStatus,
  getProductHash,
  getRegisterUrlWithHash,
  getRegisterUrlToCheckout,
  getRegisterUrl,
  getPublicUrlByPath,
  isEmailMarketingTab,
  isManualValidationNeeded,
  isNewFreeUser,
  userHasProducts,
  isFreemiumProduct,
  isPrepaidProduct,
  getProductToUpdateFromUserCollection,
  redirectMyRate,
  redirectToAndReload,
} from '@/utils/helpers';

export type ProductsPublicPageProps = {
  countries: Country[];
  products: ProductTypes[];
  freemiumProduct: ProductTypes;
  onGuestRequestInfoSubmit: (values: iFormContactGuessUserFields) => void;
  onRegisteredRequestInfoSubmit: (values: iFormContactFields) => void;
  smsCredits: SmsCredits;
  userProducts?: UserProductTypes[] | undefined;
  userData?: UserData;
  billingDetails?: BillingDetails;
  demoMode?: boolean;
  getUpdatePaymentIntent?: (
    productSelected: ProductTypes,
    userCurrency: Currency,
    intervalCount: IntervalBillingMode,
    onErrorCallback: (status: number, urlRedirect?: string, detail?: string) => void
  ) => Promise<UpdatePaymentIntent | AxiosResponse | void>;
  onCancelIncompleteAction: () => void;
  onConfirmUnpaidMeteredErrorAction: () => void;
};

export const ProductsPublicPage = ({
  countries,
  products,
  freemiumProduct,
  onGuestRequestInfoSubmit,
  onRegisteredRequestInfoSubmit,
  smsCredits,
  userProducts,
  userData,
  billingDetails,
  getUpdatePaymentIntent,
  demoMode = false,
  onCancelIncompleteAction,
  onConfirmUnpaidMeteredErrorAction,
}: ProductsPublicPageProps) => {
  const NO_CLOSE = 0;
  const { t } = useTranslation();
  const authInfo = useAuth();
  const isLogged = demoMode ? true : authInfo.isLogged();

  const { getSelectedUserProduct, setSelectedProduct } = useUserProducts();
  const {
    selectedProduct,
    selectedProductBillingMode,
    selectedProductIntervalBillingMode,
    selectedProductCurrency,
  } = getSelectedUserProduct();
  const { getMe } = useMe();

  const userProductActive = filterUserProductsByStatus(userProducts || [], ACTIVE);

  const [tabSelected, setTabSelected] = useState<ProductCategoryTypes>(PRODUCT_CATEGORY_TYPE_EMAIL);
  const [validationNeededModalIsOpen, setValidationNeededModalIsOpen] = useState(false);
  const [requestInfoModalIsOpen, setRequestInfoModalIsOpen] = useState(false);
  const [confirmNeededPaymentModalIsOpen, setConfirmNeededPaymentModalIsOpen] = useState(false);
  const [upgradeCheckoutProcessModalIsOpen, setUpgradeCheckoutProcessModalIsOpen] = useState(false);
  const [upgradeCheckoutFailProcessModalIsOpen, setUpgradeCheckoutFailProcessModalIsOpen] =
    useState(false);
  const [upgradeCheckoutSuccessProcessModalIsOpen, setUpgradeCheckoutSuccessProcessModalIsOpen] =
    useState(false);
  const [statusPaymentError, setStatusPaymentError] = useState<number>(0);
  const [
    showIncompletePaymentRequiresActionErrorModal,
    setShowIncompletePaymentRequiresActionErrorModal,
  ] = useState<boolean | string>(false);
  const [
    showIncompletePaymentMethodFailedErrorModal,
    setShowIncompletePaymentMethodFailedErrorModal,
  ] = useState<boolean | string>(false);
  const [statusUnpaidMeteredError, setStatusUnpaidMeteredError] = useState(false);
  const [statusPendingPaymentError, setStatusPendingPaymentError] = useState(false);

  const [origin, setOrigin] = useState<string | null>(null);

  const name = userData?.name || '';
  const groupId = userData?.groupId || '';
  const email = userData?.email || '';
  const countryId = billingDetails?.countryId || DEFAULT_COUNTRY_ID;
  const phone = billingDetails?.phone || DEFAULT_PHONE;

  const closeAllModals = (omit?: string) => {
    omit !== 'InfoModal' && setRequestInfoModalIsOpen(false);
    omit !== 'ValidationNeededModal' && setValidationNeededModalIsOpen(false);
    omit !== 'ConfirmNeededPaymentModal' && setConfirmNeededPaymentModalIsOpen(false);

    if (omit !== 'WaitProcessModal') {
      setUpgradeCheckoutProcessModalIsOpen(false);
      setUpgradeCheckoutSuccessProcessModalIsOpen(false);
    }

    setUpgradeCheckoutFailProcessModalIsOpen(false);
  };

  const onRequestInfoClose = () => {
    setRequestInfoModalIsOpen(false);
  };

  const onDefaultGuestRequestInfoSubmit = async (values: iFormContactGuessUserFields) => {
    onGuestRequestInfoSubmit(values);
    onRequestInfoClose();
  };

  const onDefaultUserRegisteredRequestInfoSubmit = (values: iFormContactFields) => {
    onRegisteredRequestInfoSubmit(values);
    onRequestInfoClose();
  };

  const onRequestInfo = (product: ProductTypes, origin: string | null = null) => {
    setOrigin(origin);
    setSelectedProduct(product);
    setRequestInfoModalIsOpen(true);
  };

  const onRequestInfoWithoutProduct = () => {
    setSelectedProduct(null);
    setRequestInfoModalIsOpen(true);
  };

  const onRequestAccountHero = () => {
    window.location.assign(getRegisterUrl(t('ProductsPublicPage.registerPath')));
  };

  const onFreeProductSelect = (product: ProductTypes) => {
    if (isLogged) return onRequestInfo(product);
    window.location.assign(getRegisterUrl(t('ProductsPublicPage.registerPath')));
  };

  const onRequestCloseActivationNeededModal = () => {
    setValidationNeededModalIsOpen(false);
  };

  const onShowValidationNeededModalOpen = () => {
    setValidationNeededModalIsOpen(true);
  };

  const onUpgradeCheckoutErrorCallback = (
    status: number,
    urlRedirect?: string,
    detail?: string
  ) => {
    closeAllModals();
    if (status === 402 && detail == 'requires_action')
      return setShowIncompletePaymentRequiresActionErrorModal(
        urlRedirect || PATH_UPDATE_PAYMENT_METHOD
      );
    if (status === 402 && detail == 'payment_method_failed')
      return setShowIncompletePaymentMethodFailedErrorModal(
        urlRedirect || PATH_UPDATE_PAYMENT_METHOD
      );
    if (
      status === 409 &&
      detail &&
      detail === 'There are unpaid metered items in this subscription'
    )
      return setStatusUnpaidMeteredError(status > 0);

    if (
      status === 409 &&
      detail &&
      detail === 'There are pending payments for the current subscription'
    )
      return setStatusPendingPaymentError(status > 0);
    if (
      status === 409 &&
      detail &&
      detail === 'Cannot update a subscription with an incomplete payment'
    )
      return setStatusPaymentError(status);
    setUpgradeCheckoutFailProcessModalIsOpen(true);
  };

  const onProductSelect = (product: ProductTypes) => {
    setSelectedProduct(product);

    if (!product || !selectedProductCurrency) {
      return;
    }

    if (!isLogged || !userData) {
      redirectToContractUrl(product);
      return;
    }

    if (isManualValidationNeeded(getMe()) || isNewFreeUser(userData)) {
      onShowValidationNeededModalOpen();
      return;
    }

    if (
      !isManualValidationNeeded(getMe()) &&
      (isNewFreeUser(userData) || !userHasProducts(userProducts))
    ) {
      setRequestInfoModalIsOpen(true);
      return;
    }

    onRequestPaymentModalOpen(selectedProductCurrency, product);
  };

  const redirectToContractUrl = (product: ProductTypes) =>
    window.location.assign(
      getRegisterUrlWithHash(
        t('ProductsPublicPage.contractPath'),
        getProductHash(product.mdirectorId, selectedProductCurrency, selectedProductBillingMode)
      )
    );

  const onRequestCloseConfirmNeededPaymentModal = () => {
    setConfirmNeededPaymentModalIsOpen(false);
    setStatusUnpaidMeteredError(false);
    setStatusPendingPaymentError(false);
  };

  const onActionConfirmNeededPaymentModal = async () => {
    if (
      !getUpdatePaymentIntent ||
      !selectedProduct ||
      !selectedProductCurrency ||
      !selectedProductIntervalBillingMode
    ) {
      return;
    }

    setUpgradeCheckoutProcessModalIsOpen(true);
    const updatePaymentResponse = await getUpdatePaymentIntent(
      selectedProduct,
      selectedProductCurrency,
      selectedProductIntervalBillingMode,
      onUpgradeCheckoutErrorCallback
    );

    if (updatePaymentResponse?.status === REQUEST_SUCCESS) {
      setUpgradeCheckoutProcessModalIsOpen(false);
      setUpgradeCheckoutSuccessProcessModalIsOpen(true);
    }
  };

  const onRequestPaymentModalOpen = (currency: Currency, product: ProductTypes | null) => {
    // We print the "UpdateProduct" modal always user has a product non freemium.
    if (
      userProducts &&
      userProducts.length > 0 &&
      !isFreemiumProduct(userProductActive?.product) &&
      !isPrepaidProduct(product)
    ) {
      setConfirmNeededPaymentModalIsOpen(true);
    } else {
      selectedProduct &&
        window.location.assign(
          getRegisterUrlToCheckout(
            selectedProduct,
            selectedProductCurrency,
            selectedProductIntervalBillingMode
          )
        );
    }
  };

  const getFormUserAccount = () => (
    <FormContactInModal
      isOpen={requestInfoModalIsOpen}
      shouldCloseOnOverlayClick={true}
      groupId={groupId}
      name={name}
      email={email}
      origin={origin || getOrigin(selectedProduct, selectedProductBillingMode)}
      product={getOrigin(userProductActive?.product)}
      countryId={countryId}
      phone={phone}
      onSubmit={onDefaultUserRegisteredRequestInfoSubmit}
      onRequestClose={onRequestInfoClose}
    />
  );

  const getFormContactGuest = () => (
    <FormContactGuessUserInModal
      onSubmit={onDefaultGuestRequestInfoSubmit}
      isOpen={requestInfoModalIsOpen}
      onRequestClose={onRequestInfoClose}
      shouldCloseOnOverlayClick={true}
      origin={origin || getOrigin(selectedProduct, selectedProductBillingMode)}
      countries={countries}
    />
  );

  const onComparatorLinkClick = () => {
    window.location.assign(getPublicUrlByPath(t('PublicHeader.Comparator.url')));
  };

  const timeoutCheckoutSuccessWaitProcess = () => {
    redirectMyRate();
  };

  const onUpgradeCheckoutModalClose = () => {
    setUpgradeCheckoutFailProcessModalIsOpen(false);
  };

  const onDefaultConfirmIncompleteAction = () => {
    redirectToAndReload(showIncompletePaymentRequiresActionErrorModal as string);
    setShowIncompletePaymentRequiresActionErrorModal(false);
    setShowIncompletePaymentMethodFailedErrorModal(false);
  };

  const onDefaultCancelIncompleteAction = async () => {
    onCancelIncompleteAction && (await onCancelIncompleteAction());
    setShowIncompletePaymentRequiresActionErrorModal(false);
    setShowIncompletePaymentMethodFailedErrorModal(false);
  };

  const onDefaultConfirmPendingPaymentErrorAction = () => {
    window.location.assign(PATH_UPDATE_PAYMENT_METHOD);
    onRequestCloseConfirmNeededPaymentModal && onRequestCloseConfirmNeededPaymentModal();
  };

  const onDefaultConfirmUnpaidMeteredErrorAction = () => {
    onConfirmUnpaidMeteredErrorAction && onConfirmUnpaidMeteredErrorAction();

    onRequestCloseConfirmNeededPaymentModal && onRequestCloseConfirmNeededPaymentModal();
  };

  const renderIncompletePaymentRequiresActionErrorModal = () => (
    <IncompletePaymentRequiresActionErrorModal
      isOpen={showIncompletePaymentRequiresActionErrorModal != false}
      onConfirmAction={onDefaultConfirmIncompleteAction}
      onCancelAction={onDefaultCancelIncompleteAction}
    />
  );

  const renderIncompletePaymentMethodFailedErrorModal = () => (
    <IncompletePaymentMethodFailedErrorModal
      isOpen={showIncompletePaymentMethodFailedErrorModal != false}
      onConfirmAction={onDefaultConfirmIncompleteAction}
      onCancelAction={onDefaultCancelIncompleteAction}
    />
  );

  const renderStatusUnpaidMeteredErrorModal = () => (
    <UnpaidMeteredErrorModal
      isOpen={statusUnpaidMeteredError != false}
      onConfirmAction={onDefaultConfirmUnpaidMeteredErrorAction}
      onCancelAction={onRequestCloseConfirmNeededPaymentModal}
    />
  );

  const renderStatusPendingPaymentErrorModal = () => (
    <PendingPaymentErrorModal
      isOpen={statusPendingPaymentError != false}
      onConfirmAction={onDefaultConfirmPendingPaymentErrorAction}
      onCancelAction={onRequestCloseConfirmNeededPaymentModal}
    />
  );
  const renderStatusPaymentErrorModal = () => (
    <ErrorModal
      status={statusPaymentError}
      onRequestClose={() => {
        setStatusPaymentError(0);
      }}
    />
  );

  const oldUserProduct = getProductToUpdateFromUserCollection(userProductActive, userProducts);

  return (
    <div className="flex justify-center w-full pt-2 lg:pt-10">
      <div className="w-full bg-md-white">
        {groupId ? getFormUserAccount() : getFormContactGuest()}
        <ProductsHero className="mb-14 lg:mb-4" />

        {showIncompletePaymentRequiresActionErrorModal &&
          renderIncompletePaymentRequiresActionErrorModal()}

        {showIncompletePaymentMethodFailedErrorModal &&
          renderIncompletePaymentMethodFailedErrorModal()}

        {statusPaymentError > 0 && renderStatusPaymentErrorModal()}

        {statusUnpaidMeteredError && renderStatusUnpaidMeteredErrorModal()}
        {statusPendingPaymentError && renderStatusPendingPaymentErrorModal()}

        <ActivationNeededModal
          isOpen={validationNeededModalIsOpen}
          onRequestClose={onRequestCloseActivationNeededModal}
        />
        <WaitProcessModal
          timeInMS={NO_CLOSE}
          title={t('Forms.UpgradeCheckout')}
          description={t('Forms.UpgradeCheckoutDescription')}
          isOpen={upgradeCheckoutProcessModalIsOpen}
        />
        <WaitProcessModal
          title={t('Forms.UpgradeCheckoutSuccess')}
          description={t('Forms.UpgradeCheckoutDescription')}
          isOpen={upgradeCheckoutSuccessProcessModalIsOpen}
          timeoutCallback={timeoutCheckoutSuccessWaitProcess}
        />

        <PaymentsModal
          isOpen={upgradeCheckoutFailProcessModalIsOpen}
          closeButton={false}
          style={defaultModalStyles}
        >
          <FormConfirm
            title={t('PaymentForm.upgradeFailTitle')}
            description={t('PaymentForm.upgradeFailDescription')}
            actionButton={t('Forms.accept')}
            actionButtonIcon={false}
            image={<ExclamationCircleIcon width={100} className="m-auto text-red-900" />}
            onClickAction={onUpgradeCheckoutModalClose}
          />
        </PaymentsModal>
        {confirmNeededPaymentModalIsOpen && oldUserProduct && selectedProduct && (
          <ConfirmNeededPaymentModal
            isOpen={confirmNeededPaymentModalIsOpen}
            onRequestClose={onRequestCloseConfirmNeededPaymentModal}
            onActionButton={onActionConfirmNeededPaymentModal}
            oldUserProduct={oldUserProduct}
            newUserProduct={selectedProduct}
            newCurrency={selectedProductCurrency}
            newIntervalCount={selectedProductIntervalBillingMode}
            isCouponEnabled={COUPONS_ENABLED}
            onConfirmUnpaidMeteredErrorAction={onDefaultConfirmUnpaidMeteredErrorAction}
          />
        )}
        <ProductsContainer
          products={products}
          freemiumProduct={freemiumProduct}
          userProducts={userProducts}
          onRequestInfo={onRequestInfo}
          onProductSelect={onProductSelect}
          onFreeProductSelect={onFreeProductSelect}
          tabSelected={tabSelected}
          setTabSelected={setTabSelected}
          countries={countries}
          smsCredits={smsCredits}
          userIsLogged={isLogged}
        />

        {isEmailMarketingTab(tabSelected) && (
          <div className="flex -mt-20 gap-10 items-center justify-center pb-14">
            <FeatureComparatorLink onComparatorLinkClick={onComparatorLinkClick} />
          </div>
        )}

        <HelpContainer onClick={onRequestInfoWithoutProduct} />
        {!isLogged && (
          <RequestAccountHero
            onRequestAccountClick={onRequestAccountHero}
            className="mb-16 lg:mb-0"
          />
        )}
      </div>
    </div>
  );
};
